<div class="notes-dialog" [class.notes-panel--inline]="!isDialogMode">
  <!--
    One header for both hosts, so inline and dialog share the same view toggle.
    Inline it is the card's header: its title and the pop-out sit either side.
    In a dialog the floating container already carries the title and its own
    window controls, so the row holds the toggle alone and is dropped entirely
    when there is no toggle to show.
  -->
  @if (!isDialogMode || showViewToggle) {
    <div class="notes-panel-header">
      @if (!isDialogMode) {
        <h3 class="section-title">Assignment Notes</h3>
      }
      <!--
        One view per note type, each rendering the same master-detail markup
        below; only the note set differs, so the views look identical by
        construction. Empty views are not offered, so the active view always
        has notes.
      -->
      @if (showViewToggle) {
        <mat-button-toggle-group
          [value]="activeView"
          (change)="setActiveView($event.value)"
          class="view-toggle"
          aria-label="Note type"
        >
          @for (option of availableViews; track option.view) {
            <mat-button-toggle [value]="option.view">
              {{ option.label }}
            </mat-button-toggle>
          }
        </mat-button-toggle-group>
      }
      @if (!isDialogMode) {
        <div class="page-title-actions">
          <!-- A real button, not a clickable div, so it is reachable by keyboard. -->
          <button
            type="button"
            class="title-action"
            (click)="openInFloatingDialog()"
          >
            <mat-icon class="title-action-icon">select_window_2</mat-icon>
            <span class="title-action-label">Open in dialog</span>
          </button>
        </div>
      }
    </div>
  }

  @if (isLoading) {
    <div class="loading-state">
      <mat-spinner diameter="32"></mat-spinner>
      <span>Loading assignment notes...</span>
    </div>
  } @else if (error) {
    <div class="empty-state">
      <mat-icon>error_outline</mat-icon>
      <p>{{ error }}</p>
    </div>
  } @else if (hasNoNotesAtAll) {
    <div class="empty-state">
      <mat-icon>speaker_notes_off</mat-icon>
      <p>No assignment notes.</p>
    </div>
  } @else {
    <div class="master-detail">
      <!--
        Dropped on the viewer's own single note: a one-entry list of the viewer
        themselves navigates nothing. See showSidebar for why this is keyed on
        the row count rather than the view.
      -->
      @if (showSidebar) {
        <!-- Wheel and scroll listeners are attached in code, outside the zone. -->
        <aside class="sidebar" #sidebar>
          <div class="sidebar-header">
            {{ sidebarLabel }}
            <span class="sidebar-count">({{ rows.length }})</span>
            <!--
              Only the resource/manager views need explaining — "My Note" is the
              viewer's own note and self-evident. The icon is pushed to the far
              right of the header row by margin-left:auto.

              A button, not a bare focusable mat-icon: MatIcon stamps
              aria-hidden="true" on itself unless the attribute is present
              statically, which would leave a focusable element hidden from the
              accessibility tree (axe aria-hidden-focus). The button carries the
              label and the tooltip trigger; the glyph inside stays hidden.

              The aria-label is the button's NAME and stays short. The help text
              is its DESCRIPTION, and matTooltip already publishes that through
              CDK's AriaDescriber — it stamps its own aria-describedby on this
              button pointing at an off-screen copy of the message. So the two
              are already split correctly, and binding aria-label to the help
              text would only fight that: a screen reader would read the whole
              two-clause sentence as the control's name before saying what the
              control is.
            -->
            @if (viewHelpTooltip) {
              <button
                type="button"
                class="sidebar-help-icon"
                aria-label="About these notes"
                [matTooltip]="viewHelpTooltip"
                matTooltipPosition="right"
              >
                <mat-icon aria-hidden="true">help</mat-icon>
              </button>
            }
          </div>
          <!--
            Tracked by assignment, the key the selection follows across a
            re-feed: unique within a view, since each assignment carries one
            note of each kind, and a Primary + Coverage pair are two assignments.
          -->
          @for (row of rows; track row.assignmentId) {
            <button
              type="button"
              class="sidebar-item"
              #sidebarItem
              [class.selected]="selectedIndex === $index"
              (click)="selectNote($index)"
            >
              @if (mode === "matter") {
                <!--
                  The category qualifies the name because a resource can hold
                  more than one assignment on the same matter (Primary plus
                  Coverage), and each carries its own note — without it two
                  entries here would be indistinguishable.
                -->
                <div class="item-name">
                  {{ row.fullName }}
                  @if (row.assignmentTypeCategoryName) {
                    <span class="item-qualifier"
                      >&middot; {{ row.assignmentTypeCategoryName }}</span
                    >
                  }
                </div>
              } @else {
                <app-matter-cell
                  [matterNumber]="row.clientMatterNumber ?? ''"
                  [matterName]="row.matterTitle"
                  [clientName]="row.clientName"
                />
              }
              @if (row.locked) {
                <mat-icon
                  class="item-lock"
                  matTooltip="This note is restricted due to your visibility settings."
                  >lock</mat-icon
                >
              }
            </button>
          }
        </aside>
      }
      <section class="detail-pane" #detailPane>
        @for (row of rows; track row.assignmentId) {
          <!--
            The active highlight mirrors the sidebar selection, so it is tied to
            the sidebar's presence: with no sidebar there is nothing to mirror,
            and a lone tinted note reads as a selection the reader cannot change.
          -->
          <article
            class="note-section"
            #noteSection
            [class.active]="showSidebar && selectedIndex === $index"
            [class.note-section--flush]="!showSidebar"
          >
            <div class="note-header">
              <div class="note-name">
                @if (mode === "matter") {
                  <!--
                    Matches the sidebar entry, so the two stay in step: the name
                    is bare text, not its own element, so the space before the
                    qualifier survives Angular's whitespace stripping.
                  -->
                  {{ row.fullName }}
                  @if (row.assignmentTypeCategoryName) {
                    <span class="note-name-qualifier"
                      >&middot; {{ row.assignmentTypeCategoryName }}</span
                    >
                  }
                } @else {
                  <span>{{ row.matterTitle || row.clientMatterNumber }}</span>
                }
              </div>
              @if (row.lastUpdatedName || row.lastUpdatedTimestamp) {
                <div class="note-updated">
                  <!--
                    The author is named only where it identifies someone other
                    than the reader. On the viewer's own note it is always the
                    viewer, so "Last updated" alone carries the same meaning.
                  -->
                  @if (row.lastUpdatedName && !row.isOwnNote) {
                    <span>Last updated by {{ row.lastUpdatedName }}</span>
                  } @else if (row.lastUpdatedTimestamp) {
                    <!--
                      Gated on the timestamp it introduces:
                      UserEditHistoryDto.timestamp is optional, so a row can
                      carry a name and no date, and a bare "Last updated" with
                      nothing after it says less than the name it replaced.
                    -->
                    <span>Last updated</span>
                  }
                  @if (row.lastUpdatedTimestamp) {
                    <span>
                      &middot; {{ row.lastUpdatedTimestamp | date: "MMM d, y" }}
                    </span>
                  }
                </div>
              }
            </div>
            @if (mode === "matter") {
              <div class="note-sub note-sub--matter">
                <span class="note-detail-group">
                  <mat-icon class="note-detail-icon">work</mat-icon>
                  <span class="note-detail-text">{{
                    row.title || "Not provided"
                  }}</span>
                </span>
                <span class="note-detail-sep">&middot;</span>
                <span class="note-detail-group">
                  <mat-icon class="note-detail-icon">school</mat-icon>
                  <span class="note-detail-text">
                    Class
                    @if (row.classYear) {
                      of {{ row.classYear
                      }}{{ row.seniority ? " - " + row.seniority : "" }}
                    } @else {
                      Not provided
                    }
                  </span>
                </span>
              </div>
            } @else {
              @if (row.clientName || row.clientMatterNumber) {
                <div class="note-sub note-sub--matter">
                  @if (row.clientName) {
                    <span class="note-detail-group">
                      <mat-icon class="note-detail-icon">folder_open</mat-icon>
                      <span class="note-detail-text">{{ row.clientName }}</span>
                    </span>
                  }
                  @if (row.clientName && row.clientMatterNumber) {
                    <span class="note-detail-sep">&middot;</span>
                  }
                  @if (row.clientMatterNumber) {
                    <span class="note-detail-group">
                      <span class="note-detail-text">{{
                        row.clientMatterNumber
                      }}</span>
                    </span>
                  }
                </div>
              }
            }
            @if (row.locked) {
              <span class="note-locked">
                <mat-icon>lock</mat-icon>
                This note is restricted due to your visibility settings.
              </span>
            } @else {
              <div class="note-body" [innerHTML]="row.noteHtml"></div>
            }
          </article>
        }
      </section>
    </div>
  }
</div>
